import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { API, formatCurrency, formatCurrencyPDF, formatDate, formatDateLong, STATUS_LABELS, STATUS_COLORS } from '../lib/utils';
import { getUnitLabel } from '../config/serviceCategories';
import { toast } from 'sonner';
import { 
  ChevronLeft, Download, Copy, Trash2, Send, 
  User, MapPin, Phone, Mail, FileText, Calendar,
  CheckCircle, XCircle, Clock, Receipt, Edit, AlertTriangle, RefreshCw
} from 'lucide-react';
import QuotePhotos from '../components/QuotePhotos';
import SignatureModal from '../components/SignatureModal';
import SendSignatureModal from '../components/SendSignatureModal';

const QuoteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { company, token } = useAuth();
  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [sendSignatureModalOpen, setSendSignatureModalOpen] = useState(false);

  const fetchQuote = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotes/${id}`);
      setQuote(response.data);
      // Fetch client details
      const clientRes = await axios.get(`${API}/clients/${response.data.client_id}`);
      setClient(clientRes.data);
    } catch (error) {
      toast.error('Devis non trouvé');
      navigate('/quotes');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  // Auto-open signature modal if ?action=sign is in URL (only once)
  const signActionHandled = useRef(false);
  useEffect(() => {
    if (!loading && quote && searchParams.get('action') === 'sign' && !signActionHandled.current) {
      signActionHandled.current = true;
      setSignatureModalOpen(true);
      // Clear the URL param without triggering a navigation
      window.history.replaceState({}, '', `/quotes/${id}`);
    }
  }, [loading, quote, searchParams, id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/quotes/${id}/status`, { status: newStatus });
      setQuote({ ...quote, status: newStatus });
      toast.success(`Statut mis à jour: ${STATUS_LABELS[newStatus]}`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await axios.post(`${API}/quotes/${id}/duplicate`);
      toast.success('Devis dupliqué');
      navigate(`/quotes/${response.data.id}`);
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/quotes/${id}`);
      toast.success('Devis supprimé');
      navigate('/quotes');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSendEmail = async () => {
    if (!client?.email) {
      toast.error('Le client n\'a pas d\'adresse email');
      return;
    }
    if (!company) {
      toast.error('Profil entreprise non configuré');
      return;
    }
    setSendingEmail(true);
    try {
      // Generate PDF client-side with the same design as download
      const pdfBase64 = generatePDFBase64();
      if (!pdfBase64) {
        toast.error('Erreur lors de la génération du PDF');
        setSendingEmail(false);
        return;
      }
      
      await axios.post(`${API}/send-document`, {
        document_type: 'quote',
        document_id: id,
        pdf_base64: pdfBase64
      });
      setQuote({ ...quote, status: 'envoye' });
      toast.success('Devis envoyé par email !');
      setSendDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setSendingEmail(false);
  };

  const handleConvertToInvoice = async () => {
    try {
      const response = await axios.post(`${API}/quotes/${id}/convert-to-invoice`);
      toast.success('Facture créée avec succès');
      navigate(`/invoices/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la conversion';
      toast.error(message);
    }
  };

  // Internal function that creates the PDF document and returns it
  const createPDFDocument = () => {
    if (!quote || !client || !company) return null;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 0;
    
    // Check if TVA franchise
    const isFranchise = company?.tva_regime === 'franchise';
    // Check if has capital (only for SARL, SAS, SASU, EURL)
    const formesAvecCapital = ['sarl', 'sas', 'sasu', 'eurl'];
    const showCapitalAndRCS = company?.forme_juridique && formesAvecCapital.includes(company.forme_juridique.toLowerCase());

    // Colors
    const BLUE_DARK = [30, 64, 175];       // #1E40AF
    const BORDER_GRAY = [226, 232, 240];   // #E2E8F0
    const TEXT_GRAY = [107, 114, 128];     // #6B7280
    const LIGHT_GRAY = [156, 163, 175];    // #9CA3AF
    const RED = [220, 38, 38];             // #DC2626

    // ═══ 1. HEADER - Thin blue line + minimal layout ═══
    doc.setFillColor(...BLUE_DARK);
    doc.rect(0, 0, pageWidth, 3, 'F');
    
    y = 12;
    
    // Logo (if available) - small
    if (company?.logo) {
      try {
        doc.addImage(company.logo, 'PNG', 15, y, 18, 18);
      } catch (e) {
        console.log('Logo error:', e);
      }
    }
    
    // "DEVISFAST" small gray text above "DEVIS"
    doc.setTextColor(...LIGHT_GRAY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text('DEVISFAST', company?.logo ? 38 : 15, y);
    
    // "DEVIS" in elegant blue
    doc.setTextColor(...BLUE_DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('DEVIS', company?.logo ? 38 : 15, y + 10);
    
    // Document number below
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(51, 51, 51);
    doc.text(quote.quote_number, company?.logo ? 38 : 15, y + 17);

    // Company info - right aligned
    if (company) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(51, 51, 51);
      const companyTitle = company.forme_juridique 
        ? `${company.forme_juridique.toUpperCase()} ${company.name}`
        : company.name;
      doc.text(companyTitle, pageWidth - 15, y, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...TEXT_GRAY);
      doc.text(company.address || '', pageWidth - 15, y + 5, { align: 'right' });
      doc.text(`${company.postal_code || ''} ${company.city || ''}`, pageWidth - 15, y + 10, { align: 'right' });
      doc.text(`${company.phone || ''} · ${company.email || ''}`, pageWidth - 15, y + 15, { align: 'right' });
    }

    y = 40;

    // ═══ 2. CLIENT BLOCK + DATES - Side by side ═══
    // Client block (left)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLUE_DARK);
    doc.text('CLIENT', 15, y);
    
    doc.setTextColor(51, 51, 51);
    doc.setFontSize(10);
    doc.text(client.name, 15, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_GRAY);
    if (client.company) {
      doc.text(client.company, 15, y + 11);
      doc.text(`${client.address || ''}, ${client.postal_code || ''} ${client.city || ''}`, 15, y + 16);
    } else {
      doc.text(`${client.address || ''}, ${client.postal_code || ''} ${client.city || ''}`, 15, y + 11);
    }
    
    // Dates block (right)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLUE_DARK);
    doc.text('DATES', pageWidth - 60, y);
    
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_GRAY);
    doc.setFont('helvetica', 'normal');
    doc.text("Émission :", pageWidth - 60, y + 6);
    doc.setTextColor(51, 51, 51);
    doc.text(formatDate(quote.created_at), pageWidth - 38, y + 6);
    
    doc.setTextColor(...TEXT_GRAY);
    doc.text("Validité :", pageWidth - 60, y + 12);
    doc.setTextColor(51, 51, 51);
    const validityDays = quote.validity_days || 30;
    doc.text(`${formatDate(quote.validity_date)} (${validityDays} jours)`, pageWidth - 38, y + 12);

    y += 24;

    // ═══ 3. CHANTIER BLOCK - Compact single line ═══
    if (quote.site_address || quote.reference_chantier || quote.delai_intervention) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLUE_DARK);
      doc.text('CHANTIER', 15, y);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(51, 51, 51);
      
      let chantierText = quote.site_address || '';
      y += 5;
      if (chantierText) {
        doc.text(chantierText, 15, y);
        y += 5;
      }
      
      // Ref + Delay on same line with " · "
      let refDelaiText = '';
      if (quote.reference_chantier) refDelaiText += `Réf : ${quote.reference_chantier}`;
      if (quote.delai_intervention) refDelaiText += `${refDelaiText ? ' · ' : ''}Délai : ${quote.delai_intervention}`;
      if (refDelaiText) {
        doc.setTextColor(...TEXT_GRAY);
        doc.text(refDelaiText, 15, y);
        y += 5;
      }
    }

    y += 6;

    // ═══ 4. TABLE - Clean header with blue text only, no background ═══
    // Header line
    doc.setDrawColor(...BLUE_DARK);
    doc.setLineWidth(0.3);
    doc.line(15, y, pageWidth - 15, y);
    
    y += 6;
    doc.setTextColor(...BLUE_DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text('DESCRIPTION', 15, y);
    doc.text('QTÉ', 105, y);
    doc.text('UNITÉ', 118, y);
    doc.text('P.U. HT', 138, y);
    if (!isFranchise) {
      doc.text('TVA', 158, y);
    }
    doc.text('TOTAL HT', pageWidth - 15, y, { align: 'right' });

    y += 3;
    doc.setDrawColor(...BORDER_GRAY);
    doc.setLineWidth(0.2);
    doc.line(15, y, pageWidth - 15, y);
    
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    // Table rows - no backgrounds, only horizontal separators
    quote.lines.forEach((line) => {
      if (y > pageHeight - 60) { 
        doc.addPage(); 
        y = 15; 
      }
      
      doc.setTextColor(51, 51, 51);
      const desc = line.description.length > 55 ? line.description.substring(0, 55) + '...' : line.description;
      doc.text(desc, 15, y);
      // Quantity: show "1" if empty/null
      const qty = (line.quantity || 1).toString();
      doc.text(qty, 105, y);
      const unitLabel = getUnitLabel(line.unit);
doc.text(unitLabel.includes('(') ? unitLabel.split('(')[0].trim() : unitLabel, 118, y);
      doc.text(formatCurrencyPDF(line.unit_price_ht), 138, y);
      if (!isFranchise) {
        doc.text(`${String(line.tva_rate).replace('.', ',')}%`, 158, y);
      }
      doc.text(formatCurrencyPDF(line.total_ht), pageWidth - 15, y, { align: 'right' });
      
      y += 6;
      doc.setDrawColor(...BORDER_GRAY);
      doc.line(15, y, pageWidth - 15, y);
      y += 4;
    });

    y += 4;

    // ═══ 5. TOTALS BLOCK - Compact ═══
    const totalsX = pageWidth - 70;
    const subtotalHT = quote.lines.reduce((sum, line) => sum + line.total_ht, 0);
    
    // Calculate discount amount
    let discountAmount = 0;
    if (quote.discount_value && quote.discount_value > 0) {
      discountAmount = quote.discount_type === 'percent' 
        ? subtotalHT * quote.discount_value / 100 
        : quote.discount_value;
    }
    const totalHTAfterDiscount = subtotalHT - discountAmount;
    
    // Group lines by TVA rate
    const tvaGroups = {};
    quote.lines.forEach(line => {
      const rate = line.tva_rate || 20;
      if (!tvaGroups[rate]) {
        tvaGroups[rate] = { base: 0, tva: 0 };
      }
      tvaGroups[rate].base += line.total_ht;
      tvaGroups[rate].tva += line.total_tva;
    });
    
    // Apply discount proportionally if any
    if (discountAmount > 0) {
      const rates = Object.keys(tvaGroups);
      rates.forEach(rate => {
        const proportion = tvaGroups[rate].base / subtotalHT;
        const discountForRate = discountAmount * proportion;
        tvaGroups[rate].base -= discountForRate;
        tvaGroups[rate].tva = tvaGroups[rate].base * (parseFloat(rate) / 100);
      });
    }
    
    const tvaRates = Object.keys(tvaGroups);
    const isMultiTva = tvaRates.length > 1;
    
    // Calculate total TVA
    let totalTVA = 0;
    tvaRates.forEach(rate => {
      totalTVA += tvaGroups[rate].tva;
    });
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    // Sous-total HT
    doc.setTextColor(...TEXT_GRAY);
    doc.text('Sous-total HT', totalsX, y);
    doc.setTextColor(51, 51, 51);
    doc.text(formatCurrencyPDF(subtotalHT), pageWidth - 15, y, { align: 'right' });
    y += 5;
    
    // Discount
    if (discountAmount > 0) {
      doc.setTextColor(...RED);
      doc.text(`Remise${quote.discount_type === 'percent' ? ` (${quote.discount_value}%)` : ''}`, totalsX, y);
      doc.text('-' + formatCurrencyPDF(discountAmount), pageWidth - 15, y, { align: 'right' });
      y += 5;
      
      doc.setTextColor(...TEXT_GRAY);
      doc.text('Net HT', totalsX, y);
      doc.setTextColor(51, 51, 51);
      doc.text(formatCurrencyPDF(totalHTAfterDiscount), pageWidth - 15, y, { align: 'right' });
      y += 5;
    }
    
    // TVA details (only if not franchise)
    if (!isFranchise) {
      doc.setTextColor(...TEXT_GRAY);
      doc.text('TVA', totalsX, y);
      doc.setTextColor(51, 51, 51);
      doc.text(formatCurrencyPDF(totalTVA), pageWidth - 15, y, { align: 'right' });
      y += 5;
    }
    
    y += 2;
    
    // Total TTC band - Compact
    doc.setFillColor(...BLUE_DARK);
    doc.roundedRect(totalsX - 5, y, pageWidth - totalsX + 5, 10, 1, 1, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(isFranchise ? 'TOTAL' : 'TOTAL TTC', totalsX, y + 7);
    doc.text(formatCurrencyPDF(quote.total_ttc), pageWidth - 17, y + 7, { align: 'right' });

    y += 16;

    // ═══ 6. CONDITIONS & NOTES - Compact ═══
    if (quote.payment_terms) {
      doc.setFontSize(7);
      doc.setTextColor(...TEXT_GRAY);
      doc.setFont('helvetica', 'normal');
      doc.text(`Conditions : ${quote.payment_terms}`, 15, y);
      y += 5;
    }
    
    if (quote.notes) {
      doc.setFontSize(7);
      doc.setTextColor(...TEXT_GRAY);
      const splitNotes = doc.splitTextToSize(`Notes : ${quote.notes}`, pageWidth - 30);
      doc.text(splitNotes, 15, y);
      y += splitNotes.length * 4;
    }

    // ═══ 7. SIGNATURE BLOCK - New design ═══
    y += 4;
    doc.setDrawColor(...BORDER_GRAY);
    doc.setLineWidth(0.2);
    doc.line(15, y, pageWidth - 15, y);
    y += 6;
    
    // Italic gray text
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...TEXT_GRAY);
    doc.text('Devis reçu avant exécution des travaux', 15, y);
    y += 5;
    
    // Bold text
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 51, 51);
    doc.text('Bon pour accord — Date et signature du client :', 15, y);
    y += 4;
    
    // White signature zone (minimum 60px height)
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...BORDER_GRAY);
    const sigZoneY = y;
    doc.roundedRect(15, y, 80, 25, 1, 1, 'FD');

    // ═══ 8. FOOTER - 2 lines max ═══
    doc.setFontSize(6);
    doc.setTextColor(...TEXT_GRAY);
    doc.setFont('helvetica', 'normal');
    
    // Line 1
    let footerLine1 = '';
    if (company?.forme_juridique) {
      footerLine1 = `${company.forme_juridique.toUpperCase()} ${company?.name || ''}`;
    } else {
      footerLine1 = company?.name || '';
    }
    if (company?.siret) footerLine1 += ` · SIRET ${company.siret}`;
    if (showCapitalAndRCS) {
      if (company?.capital_social) {
        footerLine1 += ` · Capital ${company.capital_social.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} €`;
      }
      if (company?.rcs_number) footerLine1 += ` · RCS ${company.rcs_city || ''} ${company.rcs_number}`;
    }
    if (isFranchise) {
      footerLine1 += ' · TVA non applicable art. 293 B CGI';
    } else if (company?.tva_number) {
      footerLine1 += ` · N°TVA ${company.tva_number}`;
    }
    doc.text(footerLine1, pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    // Line 2
    let footerLine2 = 'Pénalités retard : 3x taux légal · Recouvrement : 40 €';
    if (company?.has_decennale && company.assurance_decennale_name) {
      footerLine2 = `Décennale ${company.assurance_decennale_name} · ${footerLine2}`;
    }
    doc.text(footerLine2, pageWidth / 2, pageHeight - 6, { align: 'center' });

    // ═══ 9. PHOTOS CHANTIER (New page if photos exist) ═══
    if (quote.photos && quote.photos.length > 0) {
      doc.addPage();
      
      // Header line
      doc.setFillColor(...BLUE_DARK);
      doc.rect(0, 0, pageWidth, 3, 'F');
      
      let photoY = 15;
      
      // Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...BLUE_DARK);
      doc.text('ANNEXE – Photos chantier', 15, photoY);
      photoY += 10;
      
      // Subtitle
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...TEXT_GRAY);
      doc.text(`Devis ${quote.quote_number} · ${client?.name || ''}`, 15, photoY);
      photoY += 15;
      
      // Photos grid (max 3 photos per row, 2 rows max per page = 6 photos)
      const photoWidth = 55;
      const photoHeight = 55;
      const photoGap = 10;
      const startX = 15;
      
      quote.photos.forEach((photo, index) => {
        const col = index % 3;
        const row = Math.floor(index / 3);
        const x = startX + col * (photoWidth + photoGap);
        const py = photoY + row * (photoHeight + photoGap + 10);
        
        try {
          // Add photo
          const imgData = `data:image/jpeg;base64,${photo.data_base64}`;
          doc.addImage(imgData, 'JPEG', x, py, photoWidth, photoHeight);
          
          // Add border
          doc.setDrawColor(...BORDER_GRAY);
          doc.rect(x, py, photoWidth, photoHeight);
          
          // Add filename below
          doc.setFontSize(6);
          doc.setTextColor(...TEXT_GRAY);
          const displayName = photo.filename?.length > 20 
            ? photo.filename.substring(0, 17) + '...' 
            : (photo.filename || `Photo ${index + 1}`);
          doc.text(displayName, x + photoWidth / 2, py + photoHeight + 5, { align: 'center' });
        } catch (e) {
          console.log('Photo error:', e);
          // Draw placeholder
          doc.setFillColor(240, 240, 240);
          doc.rect(x, py, photoWidth, photoHeight, 'F');
          doc.setFontSize(8);
          doc.setTextColor(...TEXT_GRAY);
          doc.text('Photo non disponible', x + photoWidth / 2, py + photoHeight / 2, { align: 'center' });
        }
      });
      
      // Footer on photo page
      doc.setFontSize(6);
      doc.setTextColor(...TEXT_GRAY);
      doc.text(footerLine1, pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.text(footerLine2, pageWidth / 2, pageHeight - 6, { align: 'center' });
    }

    // ═══ 10. SIGNATURE (if signed) ═══
    if (quote.signature && quote.signature.image_base64) {
      doc.setPage(1);
      try {
        const sigImgData = `data:image/png;base64,${quote.signature.image_base64}`;
        doc.addImage(sigImgData, 'PNG', 17, sigZoneY + 2, 76, 21);
      } catch (e) {
        console.log('Signature image error:', e);
      }
      if (quote.signature.signed_at) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(5);
        doc.setTextColor(34, 139, 34);
        const signedDate = new Date(quote.signature.signed_at).toLocaleDateString('fr-FR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        });
        doc.text(`✓ Signé par ${client?.name || ''} le ${signedDate}`, 15, sigZoneY + 29);
      }
    }

    return doc;
  };

  // Generate PDF and download it
  const generatePDF = () => {
    const doc = createPDFDocument();
    if (!doc) return;
    doc.save(`${quote.quote_number}.pdf`);
    toast.success('PDF téléchargé');
  };

  // Generate PDF and return as base64 for email
  const generatePDFBase64 = () => {
    const doc = createPDFDocument();
    if (!doc) return null;
    const dataUri = doc.output('datauristring');
    return dataUri.split(',')[1];
  };

  if (loading) {
    return (
      <div className="page-container p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-48 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (!quote) return null;

  return (
    <div className="page-container p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/quotes')}
            className="h-10 w-10"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight uppercase">{quote.quote_number}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className={`status-badge ${STATUS_COLORS[quote.status]}`}>
                {STATUS_LABELS[quote.status]}
              </span>
              {/* Expiry warning */}
              {quote.status === 'envoye' && (() => {
                const validityDate = new Date(quote.validity_date);
                const now = new Date();
                const daysLeft = Math.ceil((validityDate - now) / (1000 * 60 * 60 * 24));
                if (daysLeft <= 7 && daysLeft > 0) {
                  return (
                    <span className="status-badge bg-blue-500/20 text-blue-400">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Expire dans {daysLeft}j
                    </span>
                  );
                } else if (daysLeft <= 0) {
                  return (
                    <span className="status-badge bg-destructive/20 text-destructive">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Expiré
                    </span>
                  );
                }
                return null;
              })()}

              {quote.status === 'envoye' && quote.signature_request && !quote.signature && (
                <>
                  <button 
                    onClick={fetchQuote}
                    className="status-badge bg-blue-500/20 text-blue-400 flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Actualiser
                  </button>
                </>
              )}
            </div>
            
          </div>
        </div>

        {/* Actions */}
        <Card className="bg-card border-white/10 mb-6">
          <CardContent className="p-4 flex flex-wrap gap-3">
            <Select value={quote.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-40 h-10 bg-muted/50 border-transparent" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                <SelectItem value="brouillon">Brouillon</SelectItem>
                <SelectItem value="envoye">Envoyé</SelectItem>
                <SelectItem value="accepte">Accepté</SelectItem>
                <SelectItem value="refuse">Refusé</SelectItem>
                <SelectItem value="expire">Expiré</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              onClick={generatePDF}
              className="border-white/10"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>

            <Button 
  variant="outline" 
  onClick={() => setSendSignatureModalOpen(true)}
  className={quote.status === 'envoye' 
    ? "border-green-500 text-green-500 hover:bg-green-500/10" 
    : "border-primary text-primary hover:bg-primary/10"}
  data-testid="send-email-btn"
>
  <Send className="w-4 h-4 mr-2" />
  {quote.status === 'envoye' ? 'Devis envoyé ✓' : 'Envoyer pour signature'}
</Button>

            <Button 
              variant="outline" 
              onClick={handleDuplicate}
              className="border-white/10"
              data-testid="duplicate-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Dupliquer
            </Button>

            {/* Edit button - only for brouillon or envoye status */}
            {['brouillon', 'envoye'].includes(quote.status) && (
              <Button 
                variant="outline"
                onClick={() => navigate(`/quotes/${id}/edit`)}
                className="border-white/10"
                data-testid="edit-quote-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            )}

            {/* Sign button - only for brouillon or envoye status and not already signed */}
            {['brouillon', 'envoye'].includes(quote.status) && !quote.signature && (
              <Button 
                onClick={() => setSignatureModalOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="sign-now-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Faire signer
              </Button>
            )}

            {/* Signed status badge and convert button - shows if signature exists OR status is accepte */}
            {(quote.signature || quote.status === 'accepte' || quote.status === 'Signé') && (
              <>
                <div className="flex items-center gap-2 px-4 py-2 bg-green-600/20 border border-green-600/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-400 font-medium">Devis signé</span>
                </div>
                {quote.invoice_id ? (
  <Button 
    onClick={() => navigate(`/invoices/${quote.invoice_id}`)}
    className="bg-green-600 hover:bg-green-700 text-white"
    data-testid="convert-to-invoice-btn"
  >
    <Receipt className="w-4 h-4 mr-2" />
    Voir la facture
  </Button>
) : (
  <Button 
    onClick={handleConvertToInvoice}
    className="bg-primary text-primary-foreground hover:bg-accent"
    data-testid="convert-to-invoice-btn"
  >
    <Receipt className="w-4 h-4 mr-2" />
    Convertir en facture
  </Button>
)}
              </>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-destructive/50 text-destructive hover:bg-destructive/20"
                  data-testid="delete-quote-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-card border-white/10">
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer ce devis ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est irréversible.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-white/10">Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Client Info */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-primary" />
              Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            {client && (
              <div className="space-y-2">
                <p className="font-semibold text-lg">{client.name}</p>
                {client.company && <p className="text-muted-foreground">{client.company}</p>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {client.address}, {client.postal_code} {client.city}
                  </span>
                  {client.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {client.phone}
                    </span>
                  )}
                  {client.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {client.email}
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quote Details */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-primary" />
                Détails du devis
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                Valide jusqu'au {formatDateLong(quote.validity_date)}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Lines */}
            <div className="space-y-2 mb-6">
              <div className="flex justify-between text-xs text-muted-foreground px-3 pb-1 border-b border-white/10">
                <span>Description</span>
                <span>Montant HT</span>
              </div>
              {quote.lines.map((line, index) => (
                <div key={line.id || index} className="bg-muted/30 rounded-lg p-3" data-testid={`detail-line-${index}`}>
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <p className="font-medium">{line.description}</p>
                    <p className="font-semibold text-primary whitespace-nowrap">{formatCurrency(line.total_ht)}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {line.quantity} × {formatCurrency(line.unit_price_ht)} / {getUnitLabel(line.unit)} • TVA {line.tva_rate}%
                  </p>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-white/10 pt-4 space-y-2">
              {company?.tva_regime === 'franchise' ? (
                <>
                  {quote.discount_value > 0 && (
                    <div className="flex justify-between text-sm text-destructive">
                      <span>Remise ({quote.discount_type === 'percent' ? `${quote.discount_value}%` : 'fixe'})</span>
                      <span>-{formatCurrency(quote.discount_type === 'percent' ? quote.total_ht * quote.discount_value / 100 : quote.discount_value)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                    <span>Total</span>
                    <span className="text-primary" data-testid="quote-total-ttc">{formatCurrency(quote.total_ttc)}</span>
                  </div>
                </>
              ) : (
                <>
                  {(() => {
                    const subtotalHT = quote.lines.reduce((s, l) => s + l.total_ht, 0);
                    const discountAmt = quote.discount_value > 0
                      ? (quote.discount_type === 'percent' ? subtotalHT * quote.discount_value / 100 : quote.discount_value)
                      : 0;
                    return (
                      <>
                        {discountAmt > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Sous-total HT</span>
                            <span>{formatCurrency(subtotalHT)}</span>
                          </div>
                        )}
                        {discountAmt > 0 && (
                          <div className="flex justify-between text-sm text-destructive">
                            <span>Remise ({quote.discount_type === 'percent' ? `${quote.discount_value}%` : 'fixe'})</span>
                            <span>-{formatCurrency(discountAmt)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{discountAmt > 0 ? 'Net HT' : 'Total HT'}</span>
                          <span>{formatCurrency(quote.total_ht)}</span>
                        </div>
                        {(() => {
                          const tvaGroups = {};
                          quote.lines.forEach(l => {
                            if (!tvaGroups[l.tva_rate]) tvaGroups[l.tva_rate] = 0;
                            tvaGroups[l.tva_rate] += l.total_tva;
                          });
                          const rates = Object.keys(tvaGroups).sort((a,b) => a-b);
                          return (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">TVA</span>
                                <span>{formatCurrency(quote.total_tva)}</span>
                              </div>
                            </>
                          );
                        })()}
                        <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                          <span>Total TTC</span>
                          <span className="text-primary" data-testid="quote-total-ttc">{formatCurrency(quote.total_ttc)}</span>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Notes & Terms */}
        {(quote.notes || quote.payment_terms) && (
          <Card className="bg-card border-white/10">
            <CardContent className="p-4 space-y-4">
              {quote.payment_terms && (
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Conditions de paiement</p>
                  <p>{quote.payment_terms}</p>
                </div>
              )}
              {quote.notes && (
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Notes</p>
                  <p className="whitespace-pre-wrap">{quote.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Photos Chantier */}
        <QuotePhotos 
          quoteId={id}
          photos={quote.photos || []}
          token={token}
          onPhotosChange={fetchQuote}
        />

        {/* Signature Modal */}
        <SignatureModal
          open={signatureModalOpen}
          onOpenChange={setSignatureModalOpen}
          quoteId={id}
          token={token}
          onSignatureComplete={fetchQuote}
        />

        {/* Send Signature Modal */}
        <SendSignatureModal
          open={sendSignatureModalOpen}
          onOpenChange={setSendSignatureModalOpen}
          quoteId={id}
          clientEmail={client?.email}
          token={token}
          onRequestSent={fetchQuote}
        />
      </div>
    </div>
  );
};

export default QuoteDetailPage;
